import { useQueryClient } from '@tanstack/react-query';

import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { CUSTOMER_CREATE_ERROR_CODES } from '@/views/customers/status/customerCreateErrorCodes.model.ts';
import { customerErrorUtil } from '@/views/customers/status/customerError.util.ts';
import { CUSTOMER_UPDATE_ERROR_CODES } from '@/views/customers/status/customerUpdateErrorCodes.model.ts';
import { useCustomerErrorToast } from '@/views/customers/status/useCustomerErrorToast.util.ts';

/**
 * The options shared by the business, foreign and individual customer creations: translated toasts of
 * the approval flow codes, and a new customer (a Prospekt by default) refreshes the pending Prospects counter.
 */
export const useCustomerCreateMutationOptions = () => {
  const queryClient = useQueryClient();
  const { handleError, meta } = useCustomerErrorToast(CUSTOMER_CREATE_ERROR_CODES);

  return {
    meta,
    mutationKey: [QueryKeyEnum.CUSTOMERS.CREATE],
    onError: handleError,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeyEnum.CUSTOMERS.PROSPECTS_COUNT] });
    },
  };
};

/**
 * The options shared by the business, foreign and individual customer updates: translated toasts of the
 * approval flow codes, and a refused update refreshes the customer, so the edit follows its current status.
 */
export const useCustomerUpdateMutationOptions = () => {
  const queryClient = useQueryClient();
  const { handleError, meta } = useCustomerErrorToast(CUSTOMER_UPDATE_ERROR_CODES);

  return {
    meta,
    mutationKey: [QueryKeyEnum.CUSTOMERS.UPDATE],
    onError: async (error: unknown, { customerId }: { customerId: number }) => {
      handleError(error);
      if (customerErrorUtil.code(error)) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeyEnum.CUSTOMERS.DETAILS, customerId],
        });
      }
    },
  };
};
